/* CUSTOM JS */

(() => {
  const CART_GET_CACHE_TTL_MS = 250;
  const CART_GET_CACHE_MAX_ENTRIES = 20;
  const CART_GET_CACHE_BYPASS_HEADER = 'x-cart-cache-bypass';

  const logCartCache = (...args) => {
    if (typeof window === 'undefined') return;
    if (window.ShopifyCartCacheLogging === false) return;
    console.debug('[cart-cache]', ...args);
  };

  const mapHeadersToObject = (map) => {
    if (!map) return {};
    return Array.from(map.entries()).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  };

  const createAbortController = () => {
    if (typeof AbortController === 'function') {
      return new AbortController();
    }
    return null;
  };

  const isProductPage = () => {
    const pageType = window.meta?.page?.pageType
      || window.ShopifyAnalytics?.meta?.page?.pageType;
    if (pageType === 'product') return true;
    if (document.body?.classList.contains('template-product')) return true;

    const pathname = window.location?.pathname || '';
    return /\/products\/[^/]+/.test(pathname);
  };

  if (isProductPage()) {
    logCartCache('cart cache interception disabled on product page', {
      pathname: window.location?.pathname
    });
    return;
  }

  const dispatchXhrEvent = (xhr, type) => {
    try {
      const event = new Event(type);
      xhr.dispatchEvent(event);
    } catch (_error) {
      // Event might not exist in some environments.
    }
  };

  const setXhrReadyState = (xhr, state) => {
    xhr.readyState = state;
    dispatchXhrEvent(xhr, 'readystatechange');
  };

  const originalFetch = window.fetch.bind(window);
  const responseCache = new Map();
  const inFlightRequests = new Map();

  const getRequestUrl = (input) => {
    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof URL) {
      return input.toString();
    }

    return input?.url;
  };

  const getRequestMethod = (input, init) => {
    const method = init?.method || input?.method || 'GET';
    return String(method).toUpperCase();
  };

  const isSameOrigin = (url) => {
    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch (_error) {
      return false;
    }
  };

  const isCartGetUrl = (url) => {
    if (!isSameOrigin(url)) return false;

    const pathname = new URL(url, window.location.origin).pathname;
    return /\/cart(\.js)?$/.test(pathname);
  };

  const isCartMutationUrl = (url) => {
    if (!isSameOrigin(url)) return false;

    const pathname = new URL(url, window.location.origin).pathname;
    return /\/cart\/(add|change|update|clear)(\.js)?$/.test(pathname);
  };

  const shouldBypassCache = (input, init, url) => {
    const headerValue = init?.headers?.[CART_GET_CACHE_BYPASS_HEADER]
      || (init?.headers?.get && init.headers.get(CART_GET_CACHE_BYPASS_HEADER))
      || (input?.headers?.get && input.headers.get(CART_GET_CACHE_BYPASS_HEADER));

    const reasons = [];
    if (init?.cache === 'no-store') {
      reasons.push('cache:no-store');
    }
    if (headerValue === '1') {
      reasons.push(`${CART_GET_CACHE_BYPASS_HEADER}`);
    }

    if (reasons.length) {
      logCartCache('cache bypassed', { url, reasons });
      return true;
    }

    return false;
  };

  const pruneExpiredEntries = () => {
    const now = Date.now();
    responseCache.forEach((entry, key) => {
      if (now - entry.createdAt > CART_GET_CACHE_TTL_MS) {
        responseCache.delete(key);
        logCartCache('expired entry removed', { key, age: now - entry.createdAt });
      }
    });
  };

  const trimCacheSize = () => {
    while (responseCache.size > CART_GET_CACHE_MAX_ENTRIES) {
      const oldestKey = responseCache.keys().next().value;
      if (!oldestKey) break;
      responseCache.delete(oldestKey);
      logCartCache('cache trimmed (capacity)', { oldestKey });
    }
  };

  const buildCacheKey = (url) => {
    const absoluteUrl = new URL(url, window.location.origin);
    absoluteUrl.search = '';
    absoluteUrl.hash = '';
    return absoluteUrl.toString();
  };

  const cloneResponseFromCache = (entry) => {
    const body = entry.body.slice(0);
    return new Response(body, {
      headers: entry.headers,
      status: entry.status,
      statusText: entry.statusText
    });
  };

  const parseCartFromEntry = (entry) => {
    if (!entry?.body) return null;
    try {
      const text = new TextDecoder().decode(entry.body);
      return JSON.parse(text);
    } catch (error) {
      logCartCache('failed to decode cached cart', { error: error.message });
      return null;
    }
  };

  const logCacheEntryDetails = (label, key, entry) => {
    if (!entry) return;
    const cart = parseCartFromEntry(entry);
    logCartCache(label, {
      key,
      status: entry.status,
      statusText: entry.statusText,
      cart
    });
  };

  const storeResponse = async (key, response) => {
    const clone = response.clone();
    const body = await clone.arrayBuffer();

    responseCache.set(key, {
      body,
      createdAt: Date.now(),
      headers: clone.headers,
      status: clone.status,
      statusText: clone.statusText
    });

    logCacheEntryDetails('response cached', key, responseCache.get(key));
    trimCacheSize();
  };

  window.fetch = async (input, init) => {
    const url = getRequestUrl(input);
    const method = getRequestMethod(input, init);

    if (!url) {
      return originalFetch(input, init);
    }

    if (method !== 'GET') {
      if (isCartMutationUrl(url)) {
        responseCache.clear();
        logCartCache('cache cleared (mutation request)', { url, method });
      }
      return originalFetch(input, init);
    }

    if (!isCartGetUrl(url)) {
      return originalFetch(input, init);
    }

    if (shouldBypassCache(input, init, url)) {
      return originalFetch(input, init);
    }

    const key = buildCacheKey(url);
    pruneExpiredEntries();

    const cachedResponse = responseCache.get(key);
    if (cachedResponse) {
      logCacheEntryDetails('cache hit', key, cachedResponse);
      return cloneResponseFromCache(cachedResponse);
    }

    const pendingRequest = inFlightRequests.get(key);
    if (pendingRequest) {
      logCartCache('dedup in-flight request', { key });
      const pendingResponse = await pendingRequest;
      logCartCache('reusing in-flight response', { key });
      return pendingResponse.clone();
    }

    logCartCache('fetching cart data', { key, url });
    const request = (async () => {
      const response = await originalFetch(input, init);
      if (response.ok) {
        await storeResponse(key, response);
      }
      return response;
    })();

    inFlightRequests.set(key, request);

    try {
      const response = await request;
      logCartCache('network response', { key, ok: response.ok, status: response.status });
      return response;
    } finally {
      inFlightRequests.delete(key);
    }
  };

  const prepareXhrResponsePayload = async (xhr, response, text) => {
    const responseType = xhr.responseType;
    if (responseType === 'json') {
      try {
        xhr.response = JSON.parse(text);
      } catch (_error) {
        xhr.response = null;
      }
    } else if (responseType === 'blob') {
      xhr.response = await response.clone().blob();
    } else if (responseType === 'arraybuffer') {
      xhr.response = await response.clone().arrayBuffer();
    } else {
      xhr.response = text;
    }
    xhr.responseText = typeof xhr.response === 'string' ? xhr.response : text;
  };

  const deliverXhrResponse = async (xhr, response, key) => {
    setXhrReadyState(xhr, 2);
    setXhrReadyState(xhr, 3);
    const text = await response.clone().text();
    xhr.status = response.status;
    xhr.statusText = response.statusText;
    xhr.responseURL = response.url;
    await prepareXhrResponsePayload(xhr, response, text);
    setXhrReadyState(xhr, 4);
    logCartCache('xhr response delivered', { key, ok: response.ok, status: response.status });
    dispatchXhrEvent(xhr, 'load');
    dispatchXhrEvent(xhr, 'loadend');
  };

  const handleXhrError = (xhr, error, key) => {
    logCartCache('xhr error', { key, error: error?.message || String(error) });
    if (error?.name === 'AbortError') {
      xhr.status = 0;
      xhr.statusText = '';
      xhr.responseText = '';
      xhr.response = null;
      setXhrReadyState(xhr, 0);
      dispatchXhrEvent(xhr, 'abort');
    } else {
      xhr.status = 0;
      xhr.statusText = '';
      xhr.responseText = '';
      xhr.response = null;
      setXhrReadyState(xhr, 4);
      dispatchXhrEvent(xhr, 'error');
    }
    dispatchXhrEvent(xhr, 'loadend');
  };

  const interceptCartXhr = async (xhr, meta) => {
    const url = meta.absoluteUrl;
    const key = buildCacheKey(url);
    logCartCache('xhr intercept start', { key, url });
    const headersObj = mapHeadersToObject(meta.headers);
    const fetchInit = {
      method: meta.method,
      headers: headersObj,
      credentials: xhr.withCredentials ? 'include' : 'same-origin',
      signal: meta.abortController?.signal
    };

    try {
      const response = await fetch(url, fetchInit);
      await deliverXhrResponse(xhr, response, key);
    } catch (error) {
      handleXhrError(xhr, error, key);
    }
  };

  if (typeof window.XMLHttpRequest === 'function') {
    const XMLHttpRequestProto = window.XMLHttpRequest.prototype;
    const originalXhrOpen = XMLHttpRequestProto.open;
    const originalXhrSend = XMLHttpRequestProto.send;
    const originalXhrSetRequestHeader = XMLHttpRequestProto.setRequestHeader;
    const originalXhrAbort = XMLHttpRequestProto.abort;

    XMLHttpRequestProto.open = function open(method, url, async = true, user, password) {
      const normalizedMethod = String(method || 'GET').toUpperCase();
      const absoluteUrl = buildCacheKey(url);
      this.__cartXhrMeta = {
        method: normalizedMethod,
        absoluteUrl,
        async: async !== false,
        headers: new Map(),
        abortController: createAbortController()
      };
      this.__cartXhrController = this.__cartXhrMeta.abortController;
      return originalXhrOpen.apply(this, arguments);
    };

    XMLHttpRequestProto.setRequestHeader = function setRequestHeader(name, value) {
      if (this.__cartXhrMeta) {
        this.__cartXhrMeta.headers.set(String(name).toLowerCase(), value);
      }
      return originalXhrSetRequestHeader.apply(this, arguments);
    };

    XMLHttpRequestProto.abort = function abort() {
      try {
        this.__cartXhrController?.abort();
      } catch (_error) {}
      return originalXhrAbort.apply(this, arguments);
    };

    XMLHttpRequestProto.send = function send() { // eslint-disable-line
      const meta = this.__cartXhrMeta;
      if (
        meta &&
        meta.method === 'GET' &&
        meta.async &&
        isCartGetUrl(meta.absoluteUrl)
      ) {
        const headersObj = mapHeadersToObject(meta.headers);
        if (shouldBypassCache(null, { headers: headersObj }, meta.absoluteUrl)) {
          return originalXhrSend.apply(this, arguments);
        }

        interceptCartXhr(this, meta);
        return;
      }

      return originalXhrSend.apply(this, arguments);
    };
  }
})();

// Reset quantity input fields to 1 when a variant is added to the cart
document.addEventListener('on:cart:add', (event) => {
  console.log('Variant added to cart', event.detail.variantId);

  const inputsQuantity = document.querySelectorAll(".qty-input__input[name='quantity']");
  inputsQuantity.forEach((input) => {
    if (input.closest('[data-card-quick-add], [data-product-quick-add]')) return;
    input.value = 1;
  });
});

// Reset quantity input fields to 1 when a variant is changed in the cart
document.addEventListener('on:line-item:change', (event) => {
  console.log('Variant changed in cart', event.detail.variantId);
  
  const inputsQuantity = document.querySelectorAll(".qty-input__input[name='quantity']");
  inputsQuantity.forEach((input) => {
    if (input.closest('[data-card-quick-add], [data-product-quick-add]')) return;
    input.value = 1;
  });
});

(() => {
  const state = {
    initialized: false,
    byVariantId: new Map(),
    pendingUpdates: 0,
    refreshDrawerOnComplete: false,
    inFlight: new Map(),
    debounceTimers: new Map(),
    desiredQty: new Map(),
    lastCart: null
  };

  const QTY_UPDATE_DEBOUNCE_MS = 300;

  const getElementHTML = (html, selector) => {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    const el = tmpl.content.querySelector(selector);
    return el ? el.innerHTML : '';
  };

  const getSectionsToRender = () => {
    let sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'free-shipping-notice',
        section: 'free-shipping-notice',
        selector: '.free-shipping-notice'
      }
    ];

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const cartDrawerSection = cartDrawer.closest('.shopify-section');
      if (cartDrawerSection) {
        const cartDrawerId = cartDrawerSection.id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      }
    }

    return sections;
  };

  const applySections = (sections, responseSections) => {
    if (!responseSections) return;
    sections.forEach(({ id, section, selector }) => {
      const sectionEl = document.getElementById(id);
      if (!sectionEl || !responseSections[section]) return;
      const targetEl = sectionEl.querySelector(selector) || sectionEl;
      targetEl.innerHTML = getElementHTML(responseSections[section], selector);
    });
  };

  const setQuickAddState = (quickAdd, quantity) => {
    const qty = Number(quantity);
    const input = quickAdd.querySelector('.qty-input__input');
    const isActive = Number.isFinite(qty) && qty > 0;
    quickAdd.classList.toggle('is-qty-active', isActive);
    quickAdd.dataset.qtySyncing = 'false';
    if (input) {
      input.value = isActive ? qty : 1;
      input.min = isActive ? '0' : '1';
    }
  };

  const updateQuickAddForVariant = (variantId, quantity) => {
    const quickAdd = state.byVariantId.get(String(variantId));
    if (!quickAdd) return;
    setQuickAddState(quickAdd, quantity);
  };

  const getVariantQuantityFromCart = (cart, variantId) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const key = String(variantId);
    const match = cart.items.find((item) => String(item.variant_id) === key);
    return match ? match.quantity : 0;
  };

  const syncQuickAddStateForVariant = async (quickAdd, variantId) => {
    const key = String(variantId);

    if (state.lastCart && Array.isArray(state.lastCart.items)) {
      setQuickAddState(quickAdd, getVariantQuantityFromCart(state.lastCart, key));
      return;
    }

    const cartUrl = window.theme?.routes?.cart || '/cart';
    try {
      const response = await fetch(`${cartUrl}.js`);
      if (!response.ok) throw new Error(response.status);
      const cart = await response.json();
      state.lastCart = cart;
      setQuickAddState(quickAdd, getVariantQuantityFromCart(cart, key));
    } catch (error) {
      console.log(error); // eslint-disable-line
      setQuickAddState(quickAdd, 0);
    }
  };

  const updateQuickAddFromCart = (cart, variantId) => {
    if (!cart || !Array.isArray(cart.items)) return;
    const key = String(variantId);
    const match = cart.items.find((item) => String(item.variant_id) === key);
    const matchQty = match ? match.quantity : 0;
    const desired = state.desiredQty.get(key);

    if (state.inFlight.get(key) && Number.isFinite(desired) && desired !== matchQty) {
      return;
    }

    updateQuickAddForVariant(variantId, matchQty);
  };

  const updateCartQuantity = async (variantId, quantity) => {
    const cartChangeUrl = window.theme?.routes?.cartChange;
    if (!cartChangeUrl) return;

    state.pendingUpdates += 1;
    const sections = getSectionsToRender();
    const payload = {
      id: variantId,
      quantity,
      sections: [...new Set(sections.map((section) => section.section))],
      sections_url: window.location.pathname
    };

    try {
      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.errors || response.status);

      state.lastCart = data;
      applySections(sections, data.sections);
      updateQuickAddFromCart(data, variantId);
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      state.pendingUpdates = Math.max(0, state.pendingUpdates - 1);
      if (state.pendingUpdates === 0 && state.refreshDrawerOnComplete) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        state.refreshDrawerOnComplete = false;
      }
    }
  };

  const scheduleCartQuantityUpdate = (variantId, quantity) => {
    const key = String(variantId);
    state.desiredQty.set(key, quantity);

    const existingTimer = state.debounceTimers.get(key);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const timer = window.setTimeout(() => {
      state.debounceTimers.delete(key);
      if (state.inFlight.get(key)) return;

      state.inFlight.set(key, true);
      const run = async () => {
        const desired = state.desiredQty.get(key);
        await updateCartQuantity(key, desired);
        const nextDesired = state.desiredQty.get(key);
        if (nextDesired !== desired) {
          return run();
        }
        state.inFlight.set(key, false);
      };

      run();
    }, QTY_UPDATE_DEBOUNCE_MS);

    state.debounceTimers.set(key, timer);
  };

  const handleInputChange = (event) => {
    const input = event.target;
    if (!input.matches('.qty-input__input') || input.name !== 'quantity') return;

    const quickAdd = input.closest('[data-card-quick-add], [data-product-quick-add]');
    if (!quickAdd) return;
    if (!quickAdd.classList.contains('is-qty-active')) return;

    const variantId = quickAdd.dataset.variantId
      || quickAdd.closest('form')?.querySelector('[name="id"]')?.value;
    const quantity = Number.parseInt(input.value, 10);
    if (Number.isNaN(quantity) || !variantId) return;

    setQuickAddState(quickAdd, quantity);
    scheduleCartQuantityUpdate(variantId, quantity);
  };

  const handleCartAdd = (event) => {
    if (event.detail?.cart) {
      state.lastCart = event.detail.cart;
    }
    updateQuickAddFromCart(event.detail.cart, event.detail.variantId);
  };

  const handleLineItemChange = (event) => {
    if (event.detail?.cart) {
      state.lastCart = event.detail.cart;
    }
    updateQuickAddForVariant(event.detail.variantId, event.detail.newQuantity);
  };

  const syncFromCart = async () => {
    const cartUrl = window.theme?.routes?.cart || '/cart';
    try {
      const response = await fetch(`${cartUrl}.js`);
      if (!response.ok) throw new Error(response.status);
      const cart = await response.json();
      state.lastCart = cart;
      state.byVariantId.forEach((quickAdd, variantId) => {
        const match = cart.items.find((item) => String(item.variant_id) === String(variantId));
        setQuickAddState(quickAdd, match ? match.quantity : 0);
      });
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  };

  const initProductCardQuickAdd = () => {
    const quickAdds = Array.from(document.querySelectorAll('[data-card-quick-add], [data-product-quick-add]'));
    if (!quickAdds.length || !window.theme?.routes?.cartChange) return;

    state.byVariantId = new Map();
    quickAdds.forEach((quickAdd) => {
      if (quickAdd.dataset.variantId) {
        state.byVariantId.set(String(quickAdd.dataset.variantId), quickAdd);
      }
    });

    if (!state.initialized) {
      document.addEventListener('change', handleInputChange);
      document.addEventListener('on:cart:add', handleCartAdd);
      document.addEventListener('on:line-item:change', handleLineItemChange);
      document.addEventListener('on:variant:change', (event) => {
        const form = event.detail?.form;
        const variant = event.detail?.variant;
        if (!form || !variant) return;
        const quickAdd = form.querySelector('[data-product-quick-add]')
          || form.closest('[data-product-quick-add]');
        if (!quickAdd) return;

        const previousVariantId = quickAdd.dataset.variantId;
        if (previousVariantId) {
          state.byVariantId.delete(String(previousVariantId));
        }

        quickAdd.dataset.variantId = variant.id;
        state.byVariantId.set(String(variant.id), quickAdd);

        // Immediately reset visual state for the new variant, then sync with cart quantity.
        setQuickAddState(quickAdd, 0);
        syncQuickAddStateForVariant(quickAdd, variant.id);
      });
      document.addEventListener('on:cart-drawer:after-open', () => {
        if (state.pendingUpdates > 0) {
          state.refreshDrawerOnComplete = true;
        }
      });
      state.initialized = true;
    }

    syncFromCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCardQuickAdd);
  } else {
    initProductCardQuickAdd();
  }

  document.addEventListener('shopify:section:load', initProductCardQuickAdd);
})();

window._axcb = window._axcb || [];
window._axcb.push(function (axeptio) {
  axeptio.on("cookies:complete", function (choices) {
    document.getElementById("axeptio_overlay").style.display = "none"; 
  });
});


/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
